/**
 * ConversationsFilters.tsx — the compact filter bar for the Conversations list.
 *
 * Mirrors `LeadsFilters`: applied filters lead as a row of removable chips, and
 * the full form lives inside a collapsed "Mais filtros" <details> disclosure
 * (default closed) so it never buries the table. v1 filters are funnel stage
 * (multi-select), has-error, and paused — NO number-search (deferred per spec).
 * Empty selection = unfiltered; ticking a funnel stage / toggle narrows the list.
 *
 * Accessibility: the funnel group is a <fieldset>/<legend>; the two toggles are
 * labelled checkboxes; the disclosure is a native <details>/<summary>; each chip
 * remove is a real button with a descriptive aria-label. Apply/Reset are real
 * buttons in a <form> so the panel is fully keyboard-operable (WCAG 2.1.1,
 * 1.3.1, 4.1.2).
 */

import { useCallback, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FUNNEL_STAGES, type FunnelStage } from '../../api/types';
import type { ConversationsQuery } from '../../api/client';
import { Button } from '../../components/Button';
import { FilterChips, type FilterChip } from '../../components/FilterChips';

export interface ConversationsFiltersProps {
  /** Called with a resolved query when the operator applies filters. */
  onApply: (query: ConversationsQuery) => void;
  /** Whether a fetch is in flight (disables Apply to avoid overlap). */
  busy: boolean;
  /** The last APPLIED query — drives the removable chip row. */
  applied: ConversationsQuery;
}

const DEFAULT_LIMIT = 50;

interface FormState {
  stages: Set<FunnelStage>;
  hasError: boolean;
  paused: boolean;
}

const emptyForm = (): FormState => ({
  stages: new Set<FunnelStage>(),
  hasError: false,
  paused: false,
});

const queryToForm = (q: ConversationsQuery): FormState => ({
  stages: new Set(q.funnel_stage ?? []),
  hasError: q.has_error === true,
  paused: q.paused === true,
});

export const ConversationsFilters = ({ onApply, busy, applied }: ConversationsFiltersProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => queryToForm(applied));

  const toggleStage = useCallback((stage: FunnelStage) => {
    setForm((prev) => {
      const stages = new Set(prev.stages);
      if (stages.has(stage)) stages.delete(stage);
      else stages.add(stage);
      return { ...prev, stages };
    });
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onApply({
        funnel_stage: [...form.stages],
        has_error: form.hasError,
        paused: form.paused,
        limit: DEFAULT_LIMIT,
      });
    },
    [form, onApply],
  );

  const handleReset = useCallback(() => {
    setForm(emptyForm());
    onApply({ limit: DEFAULT_LIMIT });
  }, [onApply]);

  const applyNarrowed = useCallback(
    (next: ConversationsQuery) => {
      onApply(next);
      setForm(queryToForm(next));
    },
    [onApply],
  );

  // --- Applied-filter chips ------------------------------------------------
  const chips: FilterChip[] = [];
  const removeChipLabel = (label: string): string => t('filters.remove', { label });
  (applied.funnel_stage ?? []).forEach((stage) => {
    const label = t(`funnelStage.${stage}`);
    chips.push({
      key: `stage:${stage}`,
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () =>
        applyNarrowed({
          ...applied,
          funnel_stage: (applied.funnel_stage ?? []).filter((s) => s !== stage),
        }),
    });
  });
  if (applied.has_error === true) {
    const label = t('conversations.filters.hasError');
    chips.push({
      key: 'has_error',
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, has_error: false }),
    });
  }
  if (applied.paused === true) {
    const label = t('conversations.filters.paused');
    chips.push({
      key: 'paused',
      label,
      removeLabel: removeChipLabel(label),
      onRemove: () => applyNarrowed({ ...applied, paused: false }),
    });
  }

  return (
    <form
      aria-label={t('conversations.filters.legend')}
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-xl bg-surface-2 px-3 py-1 ring-1 ring-inset ring-ink-3/10"
    >
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-meta uppercase tracking-wide text-ink-3">
            {t('filters.active')}
          </span>
          <FilterChips chips={chips} testid="conversations-filter-chips" />
        </div>
      ) : null}

      <details className="flex flex-col gap-4" data-testid="conversations-filters-disclosure">
        <summary className="flex min-h-11 cursor-pointer select-none items-center text-body font-medium text-ink-1 marker:text-ink-3">
          {t('filters.more')}
        </summary>

        <div className="mt-3 flex flex-col gap-4">
          <fieldset className="flex flex-col gap-2">
            <legend className="text-meta uppercase tracking-wide text-ink-3">
              {t('conversations.filters.funnelStage')}
            </legend>
            <p className="text-meta text-ink-3">{t('conversations.filters.funnelHint')}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {FUNNEL_STAGES.map((stage) => (
                <label
                  key={stage}
                  htmlFor={`conversations-filter-stage-${stage}`}
                  className="flex min-h-6 items-center gap-1.5 text-body text-ink-1"
                >
                  <input
                    type="checkbox"
                    id={`conversations-filter-stage-${stage}`}
                    name="funnel_stage"
                    value={stage}
                    checked={form.stages.has(stage)}
                    onChange={() => toggleStage(stage)}
                    className="h-4 w-4 rounded border-ink-3 bg-surface-1 text-gold focus:ring-outline-focus"
                  />
                  {t(`funnelStage.${stage}`)}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-meta uppercase tracking-wide text-ink-3">
              {t('conversations.filters.flags')}
            </legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <label
                htmlFor="conversations-filter-has-error"
                className="flex min-h-6 items-center gap-1.5 text-body text-ink-1"
              >
                <input
                  type="checkbox"
                  id="conversations-filter-has-error"
                  name="has_error"
                  checked={form.hasError}
                  onChange={(e) => setForm((p) => ({ ...p, hasError: e.target.checked }))}
                  className="h-4 w-4 rounded border-ink-3 bg-surface-1 text-gold focus:ring-outline-focus"
                />
                {t('conversations.filters.hasError')}
              </label>
              <label
                htmlFor="conversations-filter-paused"
                className="flex min-h-6 items-center gap-1.5 text-body text-ink-1"
              >
                <input
                  type="checkbox"
                  id="conversations-filter-paused"
                  name="paused"
                  checked={form.paused}
                  onChange={(e) => setForm((p) => ({ ...p, paused: e.target.checked }))}
                  className="h-4 w-4 rounded border-ink-3 bg-surface-1 text-gold focus:ring-outline-focus"
                />
                {t('conversations.filters.paused')}
              </label>
            </div>
          </fieldset>
        </div>
      </details>

      <div className="flex gap-3">
        <Button
          type="submit"
          variant="primary"
          data-testid="conversations-apply"
          disabled={busy}
          aria-busy={busy}
          className="min-h-11"
        >
          {t('conversations.filters.apply')}
        </Button>
        <Button
          data-testid="conversations-reset"
          onClick={handleReset}
          disabled={busy}
          className="min-h-11"
        >
          {t('conversations.filters.reset')}
        </Button>
      </div>
    </form>
  );
};
